"use client";

import { useEffect } from "react";
import { usePathname, useParams } from "next/navigation";
import Intercom, { update as updateIntercom } from "@intercom/messenger-js-sdk";
import useUserInfo from "@/hooks/useUserInfo";
import { TECH_WIZARD_PATH } from "@/features/tech-onboarding/constants";

const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

interface IntercomUser {
  userId: string;
  userName: string;
  email: string;
  createdAt?: number;
}

/** The identity verification JWT for the signed in user, or null when the server does not give one. */
async function identityJwt(): Promise<string | null> {
  try {
    const res = await fetch("/api/intercom/identity");
    if (!res.ok) return null;
    const { jwt } = await res.json();
    return typeof jwt === "string" ? jwt : null;
  } catch {
    return null;
  }
}

/** Boots the messenger as a signed in user, with the identity verification JWT when the server gives one. */
async function bootAsUser(user: IntercomUser, hideLauncher: boolean) {
  if (!APP_ID) return;
  const basePayload: Parameters<typeof Intercom>[0] = {
    app_id: APP_ID,
    user_id: user.userId,
    name: user.userName,
    email: user.email,
    hide_default_launcher: hideLauncher,
    ...(user.createdAt != null && { created_at: user.createdAt }),
  };
  const jwt = await identityJwt();
  Intercom(jwt ? { ...basePayload, api_base: "https://api-iam.intercom.io", intercom_user_jwt: jwt } : basePayload);
}

/**
 * Tells a messenger that booted as a visitor who the customer is, once they sign in on that same page (the Tech
 * BoxScore wizard's account step). Booting again does nothing (the SDK starts once per page), so this updates the
 * running messenger. Does nothing when Intercom is not running or nobody is signed in.
 */
export async function identifyIntercomUser(): Promise<void> {
  if (!APP_ID || typeof window === "undefined" || !window.Intercom) return;
  const user = await signedInUser();
  if (!user) return;
  const jwt = await identityJwt();
  updateIntercom({
    user_id: user.userId,
    name: user.userName,
    email: user.email,
    ...(user.createdAt != null && { created_at: user.createdAt }),
    ...(jwt && { intercom_user_jwt: jwt }),
  });
}

// The Tech BoxScore wizard is reached before sign in, so it stays an auth path (no /api/user/me through lib/axios),
// but most of it runs signed in. There a plain fetch tries to identify the customer, so the team sees who is writing
// in the chat; a 401 leaves them a visitor, with no token refresh and no redirect to /login.
function isTechWizardPath(pathname: string | null): boolean {
  return !!pathname && (pathname === TECH_WIZARD_PATH || pathname.startsWith(TECH_WIZARD_PATH + "/"));
}

async function signedInUser(): Promise<IntercomUser | null> {
  try {
    const res = await fetch("/api/user/me", { credentials: "same-origin" });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.userId || !data?.email) return null;
    return { userId: data.userId, userName: data.userName ?? "", email: data.email, createdAt: data.createdAt };
  } catch {
    return null;
  }
}

// "/login-m" is the superSuperAdmin impersonation login - same auth surface as "/login",
// so it must skip /api/user/me too (otherwise: 401 → refresh fail → logout → redirect to /login).
// /tech-boxscore: the Tech BoxScore wizard and guide, both reached before sign in.
// /tv: the public TV board (share link + code, no login); a 401 there would bounce the TV to /login.
const AUTH_PATHS = ["/login", "/login-m", "/register", "/forgot-password", "/reset-password", "/tech-boxscore", "/tv"];

export function isAuthPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return AUTH_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"));
}

// Wall-mounted TV board: no chat launcher at all, not even as a visitor.
function isTvPath(pathname: string | null): boolean {
  return !!pathname && pathname.startsWith("/tv/");
}

export default function IntercomProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const params = useParams();
  const onAuthRoute = isAuthPath(pathname);
  const onTvRoute = isTvPath(pathname);
  const onTechWizard = isTechWizardPath(pathname);
  // Hide the default launcher on pages with companyId (sidebar has a Support button there)
  const hasCompanyId = Boolean(params?.companyId);
  // Skip /api/user/me on auth routes to avoid 401 → redirect loop; useUserInfo accepts optional { enabled }
  const { userId, userName, email, createdAt, loading } = useUserInfo(
    { enabled: !onAuthRoute } as Parameters<typeof useUserInfo>[0],
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (!APP_ID) return;
    if (onTvRoute) return;

    // The Tech BoxScore wizard: the signed in customer when there is one, else a visitor (see isTechWizardPath).
    if (onTechWizard) {
      let cancelled = false;
      void signedInUser().then(user => {
        if (cancelled) return;
        if (user) void bootAsUser(user, false);
        else Intercom({ app_id: APP_ID });
      });
      return () => {
        cancelled = true;
      };
    }

    // On login/register etc. boot as visitor immediately so we never call /api/user/me (avoids 401 → redirect loop)
    if (onAuthRoute) {
      Intercom({ app_id: APP_ID });
      return;
    }

    if (loading) return;
    if (userId && userName != null && email) {
      // Logged-in user: boot with user data; optionally add JWT for identity verification
      void bootAsUser({ userId, userName, email, createdAt }, hasCompanyId);
    } else {
      // Anonymous visitor (e.g. other public pages)
      Intercom({ app_id: APP_ID });
    }
  }, [onAuthRoute, onTvRoute, onTechWizard, userId, userName, email, createdAt, loading, hasCompanyId]);

  return <>{children}</>;
}
